import React, { useEffect, useState } from 'react';
import useActionableCondition from './useActionableCondition';
import NoDataFound from '../NoDataFound/NoDataFound';
import { useOption } from '../../../../context/OptionContext';

const ActionableConditionCard = ({ formattedStart, formattedEnd, isButtonDisabled, selectedIds , selectedTab }) => {
  const { loading, categoryPerformanceState, getCategoryPerformanceDashbaord} = useActionableCondition();

  const [showAll, setShowAll] = useState(false);
  const [visibleCount, setVisibleCount] = useState(6);
  const [activeTooltipIndex, setActiveTooltipIndex] = useState(null);
  const [prevData, setPrevData] = useState({});
  const {selectedOption} = useOption()

  useEffect(() => {
    getCategoryPerformanceDashbaord({ formattedEnd, formattedStart , selectedTab });
  }, [formattedStart, formattedEnd , selectedTab,selectedOption?.id]);

  useEffect(() => {
    if (isButtonDisabled) {
      getCategoryPerformanceDashbaord({ formattedEnd, formattedStart, selectedIds, selectedTab});
    }
  }, [isButtonDisabled]);

  useEffect(() => {
    if (!loading && categoryPerformanceState && Object.keys(categoryPerformanceState).length > 0) {
      setPrevData(categoryPerformanceState);
    }
  }, [loading, categoryPerformanceState]);

  useEffect(() => {
    const calculateVisibleCount = () => {
      const width = window.innerWidth;
      if (width >= 1800) return 6;
      if (width >= 1600) return 5;
      if (width >= 1400) return 4;
      if (width >= 1200) return 3;
      if (width >= 768) return 2;
      if (width >= 389) return 1;
      return 2;
    };

    const updateVisibleCount = () => setVisibleCount(calculateVisibleCount());
    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, []);

  const transformKey = (key) => {
    const keyWithSpaces = key.replace(/_/g, ' ');
    const lowerKey = key.toLowerCase();
    if ((lowerKey.includes("roas") || lowerKey.includes("acos")) && !lowerKey.includes("tacos")) {
      return keyWithSpaces.toUpperCase();
    }
    return keyWithSpaces.split(' ').map(word =>
      word.toLowerCase() === 'tacos' ? 'TACoS' : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const isEmpty = !prevData || Object.keys(prevData).length === 0;
  const allCards = Object.entries(prevData);
  const initialCards = allCards.slice(0, visibleCount);
  const remainingCards = allCards.slice(visibleCount);
  const displayedCards = showAll ? allCards : initialCards;

  const toggleTooltip = (index) => {
    setActiveTooltipIndex(prev => (prev === index ? null : index));
  };

  return (
    <div className="dashboard-grid">
      {isEmpty ? (
        loading ? (
          Array.from({ length: visibleCount }).map((_, index) => (
            <div className="card p-3 shadow-sm" key={index}>
              <div className="skeleton skeleton-text w-100 mb-2" />
              <div className="skeleton skeleton-text w-75 mb-1" />
              <div className="skeleton skeleton-text w-50" />
            </div>
          ))
        ) : (
          <div style={{ textAlign: "center", padding: "30px", fontWeight: "500" }}>
            No data found
          </div>
        )
      ) : (
        <>
          {displayedCards.map(([key, card], index) => {
            const pastChangeValue = parseFloat(card.past_change);
            const isNegative = !isNaN(pastChangeValue) && pastChangeValue < 0;

            return (
              <div className="card p-3 shadow-sm" key={index} style={{ position: 'relative' }}>
                <div className="icon-wrapper mb-3 d-flex justify-content-between">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: '50px',
                      height: '50px',
                      backgroundColor: '#e6f0fa',
                      color: '#007bff'
                    }}
                  >
                    <i className={`bi ${card.icon || 'bi-bar-chart'} fs-4`}></i>
                  </div>

                  <div className="info-tooltip-wrapper position-relative">
                    <i
                      className="bi bi-info-circle ms-2 text-secondary"
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleTooltip(index)}
                    ></i>
                    {activeTooltipIndex === index && (
                      <div className="info-tooltip-box">
                        <strong>Sales Rules:</strong>
                        <ul className="mt-2 mb-0 ps-3">
                          <li><b>Gross Sales</b>: Total without deductions</li>
                          <li><b>Net Sales</b>: Gross minus returns/discounts</li>
                          <li><b>Past Change</b>: Compared to previous period</li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Value */}
                <div className="price-tooltip-wrapper position-relative d-inline-block">
                  <h4 className="fw-bold mb-1" style={{ cursor: "pointer" }}>
                    {loading ? <div className="skeleton skeleton-text w-75" /> : card.value}
                  </h4>
                  {!loading && card.tooltip && (
                    <div className="price-tooltip-box">{card.tooltip}</div>
                  )}
                </div>

                {/* Title */}
                <p className="mb-1 small">
                  {transformKey(key)}
                  {!loading && 'change' in card && (
                    <span className="text-success ms-1" style={{ fontSize: "x-small" }}>
                      ({card.change})
                    </span>
                  )}
                </p>

                {/* Past Change */}
                <span
                  className={`text-${isNegative ? 'danger' : 'success'}`}
                  style={{ fontSize: 'x-small' }}
                >
                  {loading ? (
                    <div className="skeleton skeleton-text w-50" />
                  ) : (
                    card?.past_change || ""
                  )}
                </span>
              </div>
            );
          })}

          {/* Show More Card */}
          {allCards.length > visibleCount && (
            <div
              className="card p-3 shadow-sm d-flex align-items-center justify-content-center text-center"
              style={{ cursor: 'pointer', minHeight: '150px' }}
              onClick={() => setShowAll(!showAll)}
            >
              <i className={`bi ${showAll ? 'bi-dash-circle' : 'bi-plus-circle'} fs-3 text-primary mb-2`}></i>
              <p className="mb-0 fw-bold text-primary">
                {showAll ? 'Show Less' : `Show ${remainingCards.length} More`}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ActionableConditionCard;
